import React from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { Heading } from "@/components/heading";
import { Button } from "@/components/button";
import { Text } from "@/components/text";
import { ArrowLeftIcon } from "@heroicons/react/20/solid";
import PageContainer from "@/components/layouts/PageContainer";
import ScoreConversionTableForm from "./components/ScoreConversionTableForm";
import { useCreateScoreConversionTableMutation } from "@/apis/score_conversion_tables";
import { useQueryGetExamPaper } from "@/apis/exam_papers/getExamPaper";
import type { ICreateScoreConversionTableRequest } from "@/apis/score_conversion_tables/types";

const ScoreConversionTableCreatePage: React.FC = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const examPaperId = searchParams.get('exam_paper_id');
  const createMutation = useCreateScoreConversionTableMutation();
  
  // Query exam paper to get total question count
  const { data: examPaper } = useQueryGetExamPaper(examPaperId || "", {
    enabled: !!examPaperId
  });

  // Calculate total question count from exam paper sections
  const totalQuestions = React.useMemo(() => {
    if (!examPaper?.sections) return 0;
    
    return examPaper.sections.reduce((total, section) => {
      return total + (Array.isArray(section.question_ids) ? section.question_ids.length : 0);
    }, 0);
  }, [examPaper]);

  const handleSubmit = async (data: ICreateScoreConversionTableRequest) => {
    try {
      // exam_paper_id가 URL 파라미터로 제공된 경우 추가
      const requestData = examPaperId 
        ? { ...data, exam_paper_id: examPaperId }
        : data;
        
      await createMutation.mutateAsync(requestData);
      navigate(-1); // Go back to previous page
    } catch (error) {
      console.error("Failed to create score conversion table:", error);
    }
  };

  const handleCancel = () => {
    navigate(-1); // Go back to previous page
  };

  return (
    <PageContainer>
      <div className="space-y-6">
        <div className="flex items-center gap-4">
          <Button plain onClick={() => navigate(-1)}>
            <ArrowLeftIcon className="h-5 w-5" />
            Back
          </Button>
          <div>
            <Heading>Create Score Conversion Table</Heading>
      
            <Text className="text-gray-600">
              Create a new score conversion table for converting raw scores to scale scores.
              {examPaper && (
                <span className="block mt-1 text-blue-600 font-medium">
                  Total Questions: {totalQuestions}
                </span>
              )}
            </Text>
          </div>
        </div>

        <ScoreConversionTableForm
          onSubmit={handleSubmit}
          onCancel={handleCancel}
          isLoading={createMutation.isPending}
          totalQuestions={totalQuestions}
        />
      </div>
    </PageContainer>
  );
};

export default ScoreConversionTableCreatePage; 